import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { STRIPE_PROXY_URL } from '../../config/billing';

interface Props {
  open: boolean;
  onClose: () => void;
  subscriptionId: string | null;
  customerName?: string;
}

const fmtAmount = (cents: number | null | undefined) =>
  cents == null ? '—' : `$${(cents / 100).toFixed(2)}`;

const fmtDate = (ts: number | null | undefined) =>
  ts ? new Date(ts * 1000).toLocaleDateString() : '—';

const statusColor = (s: string): any => {
  switch (s) {
    case 'active': case 'trialing': case 'paid': return 'green';
    case 'past_due': case 'unpaid': case 'open': return 'yellow';
    case 'canceled': case 'incomplete_expired': case 'void': return 'red';
    default: return 'gray';
  }
};

export const SubscriptionDetailModal: React.FC<Props> = ({ open, onClose, subscriptionId, customerName }) => {
  const [sub, setSub] = useState<any>(null);
  const [upcoming, setUpcoming] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !subscriptionId) return;
    setLoading(true); setError(''); setSub(null); setUpcoming(null);
    Promise.all([
      fetch(`${STRIPE_PROXY_URL}/subscriptions/detail?subscription_id=${encodeURIComponent(subscriptionId)}`).then(r => r.json()),
      fetch(`${STRIPE_PROXY_URL}/invoices/upcoming?subscription=${encodeURIComponent(subscriptionId)}`).then(r => r.json()).catch(() => null),
    ])
      .then(([s, inv]) => {
        if (!s || s?.error) { setError(s?.error?.message || s?.error || 'Failed to load subscription'); return; }
        setSub(s);
        if (inv && !inv.error) setUpcoming(inv);
      })
      .catch((e) => setError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [open, subscriptionId]);

  const items = sub?.items?.data || [];
  const lineSubtotal = items.reduce((sum: number, i: any) => sum + (i.price?.unit_amount || 0) * (i.quantity || 1), 0);

  // Pull richer totals from upcoming/latest invoice when available.
  const inv = upcoming || sub?.latest_invoice || {};
  const subtotal = inv.subtotal ?? lineSubtotal;
  const tax = inv.tax ?? 0;
  const total = inv.total ?? (subtotal + tax);
  const amountDue = inv.amount_due ?? total;
  const discountAmount =
    (inv.total_discount_amounts || []).reduce((sum: number, d: any) => sum + (d.amount || 0), 0)
    || (sub?.discount?.coupon?.amount_off || 0);
  // Service fees / application fees
  const applicationFee = sub?.application_fee_percent
    ? Math.round(subtotal * (sub.application_fee_percent / 100))
    : (inv.application_fee_amount || 0);

  return (
    <Modal open={open} onClose={onClose} title={`Subscription · ${customerName || subscriptionId?.slice(-8) || ''}`} size="lg">
      {loading ? (
        <div className="py-12 text-center text-gray-500"><div className="animate-spin h-8 w-8 border-2 border-brand-600 border-t-transparent rounded-full inline-block" /></div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>
      ) : !sub ? null : (
        <div className="space-y-5">
          {/* ─── Header ─── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500 uppercase">Subscription ID</p>
              <p className="font-mono text-xs text-gray-900 break-all">{sub.id}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Status</p>
              <Badge color={statusColor(sub.status)}>{sub.status}</Badge>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Started</p>
              <p className="text-gray-900">{fmtDate(sub.start_date || sub.created)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Current Period</p>
              <p className="text-gray-900 text-xs">{fmtDate(sub.current_period_start)} – {fmtDate(sub.current_period_end)}</p>
            </div>
          </div>

          {/* ─── Line items ─── */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Line Items</h4>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-left">Stripe Product ID</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Unit Price</th>
                    <th className="px-3 py-2 text-right">Extended</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((i: any) => {
                    const productObj = i.price?.product;
                    const productId = typeof productObj === 'string' ? productObj : productObj?.id;
                    const productName = (typeof productObj === 'object' ? productObj?.name : null) || i.price?.nickname || i.description || '—';
                    const qty = i.quantity || 1;
                    const unit = i.price?.unit_amount || 0;
                    const ext = unit * qty;
                    const interval = i.price?.recurring?.interval ? `/${i.price.recurring.interval}` : '';
                    return (
                      <tr key={i.id} className="bg-white">
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{productName}</div>
                          {i.price?.nickname && productName !== i.price.nickname && <div className="text-xs text-gray-500">{i.price.nickname}</div>}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-600">{productId || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{qty}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtAmount(unit)}{interval}</td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmtAmount(ext)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ─── Totals ─── */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
              {upcoming ? 'Upcoming Invoice' : sub.latest_invoice ? 'Latest Invoice' : 'Totals'}
            </h4>
            <div className="border border-gray-200 rounded-lg p-4 space-y-1.5 text-sm bg-gray-50">
              <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-medium tabular-nums">{fmtAmount(subtotal)}</span></div>

              {discountAmount > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Discount{sub.discount?.coupon?.name ? ` — ${sub.discount.coupon.name}` : ''}{sub.discount?.coupon?.percent_off ? ` (${sub.discount.coupon.percent_off}% off)` : ''}</span>
                  <span className="tabular-nums">-{fmtAmount(discountAmount)}</span>
                </div>
              )}

              {(inv.total_tax_amounts || []).length > 0 ? (
                inv.total_tax_amounts.map((t: any, idx: number) => (
                  <div key={idx} className="flex justify-between">
                    <span className="text-gray-600">{t.tax_rate?.display_name || 'Tax'} {t.tax_rate?.percentage != null ? `(${t.tax_rate.percentage}%)` : ''}</span>
                    <span className="tabular-nums">{fmtAmount(t.amount)}</span>
                  </div>
                ))
              ) : tax > 0 && (
                <div className="flex justify-between"><span className="text-gray-600">Tax</span><span className="tabular-nums">{fmtAmount(tax)}</span></div>
              )}

              {applicationFee > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Service Fee{sub.application_fee_percent ? ` (${sub.application_fee_percent}%)` : ''}</span>
                  <span className="tabular-nums">{fmtAmount(applicationFee)}</span>
                </div>
              )}

              <div className="flex justify-between border-t border-gray-300 pt-2 mt-1">
                <span className="font-semibold text-gray-900">Total</span>
                <span className="font-bold text-gray-900 tabular-nums">{fmtAmount(total)}</span>
              </div>
              {amountDue !== total && (
                <div className="flex justify-between"><span className="text-gray-600">Amount Due</span><span className="font-semibold text-gray-900 tabular-nums">{fmtAmount(amountDue)}</span></div>
              )}
            </div>
            {!upcoming && !sub.latest_invoice && (
              <p className="text-xs text-gray-500 mt-2">No invoice generated yet — totals shown reflect line-item subtotal only.</p>
            )}
          </div>

          {/* ─── Payment method ─── */}
          {sub.default_payment_method?.card && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Payment Method</h4>
              <div className="text-sm text-gray-700">
                {sub.default_payment_method.card.brand?.toUpperCase()} •••• {sub.default_payment_method.card.last4}
                <span className="text-gray-400 ml-2">exp {sub.default_payment_method.card.exp_month}/{sub.default_payment_method.card.exp_year}</span>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-3 border-t border-gray-200">
            <a
              href={`https://dashboard.stripe.com/test/subscriptions/${sub.id}`}
              target="_blank" rel="noreferrer"
              className="text-sm text-brand-600 hover:text-brand-700 font-medium mr-auto"
            >Open in Stripe ↗</a>
            <button onClick={onClose} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50">Close</button>
          </div>
        </div>
      )}
    </Modal>
  );
};
